/**
 * The embeddable-component API of @neuro/plugin-sdk (≥ 1.4).
 *
 * Imported as a namespace and feature-detected so the host keeps running —
 * with embedding disabled — against an older SDK, e.g. a dev server whose
 * pre-bundled dependency cache predates 1.4. Once 1.4 is the minimum
 * version this can become plain named imports.
 */

import * as SdkUi from '@neuro/plugin-sdk/ui';
import type {
  EmbeddableComponentDescriptor,
  EmbeddableComponentRegistry,
  EmbedBridge,
} from '@neuro/plugin-sdk/ui';
import type { Context } from 'react';

type SdkUiModule = typeof import('@neuro/plugin-sdk/ui');

export interface EmbedSdk {
  registry: EmbeddableComponentRegistry;
  EmbedInstanceContext: Context<EmbedBridge | null>;
  useEmbeddableComponent: SdkUiModule['useEmbeddableComponent'];
  normalizeUiComponentDeclaration: SdkUiModule['normalizeUiComponentDeclaration'];
  extractRouteParams: SdkUiModule['extractRouteParams'];
  matchRoutePattern: SdkUiModule['matchRoutePattern'];
  fillRoutePattern: SdkUiModule['fillRoutePattern'];
  gridUnitsToPx: SdkUiModule['gridUnitsToPx'];
  parseEntrypoint: SdkUiModule['parseEntrypoint'];
}

let cached: EmbedSdk | null | undefined;

export function getEmbedSdk(): EmbedSdk | null {
  if (cached !== undefined) return cached;
  const sdk = SdkUi as Partial<SdkUiModule>;
  if (!sdk.EmbeddableComponentRegistry || !sdk.EmbedInstanceContext || !sdk.normalizeUiComponentDeclaration) {
    cached = null;
    return cached;
  }
  cached = {
    registry: sdk.EmbeddableComponentRegistry.getInstance(),
    EmbedInstanceContext: sdk.EmbedInstanceContext,
    useEmbeddableComponent: sdk.useEmbeddableComponent!,
    normalizeUiComponentDeclaration: sdk.normalizeUiComponentDeclaration,
    extractRouteParams: sdk.extractRouteParams!,
    matchRoutePattern: sdk.matchRoutePattern!,
    fillRoutePattern: sdk.fillRoutePattern!,
    gridUnitsToPx: sdk.gridUnitsToPx!,
    parseEntrypoint: sdk.parseEntrypoint!,
  };
  return cached;
}

export type { EmbeddableComponentDescriptor };
